import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import RepairRequestOrderCard from '../components/Request/RepairRequestOrderCard';
import {
  PageErrorState,
  PageLoadingState,
  PageNotFoundState,
} from '../components/common/ViewStates';
import { Section } from '../components/common/UIComponents';
import { useAuth } from '../contexts/AuthContext';
import { repairRequestService } from '../services/repairRequestService';
import { reviewService } from '../services/reviewService';
import { serviceProviderService } from '../services/serviceProviderService';
import type { RepairRequest, Review } from '../types/firestore';
import { RepairStatus } from '../types/firestore';
import { isApprovedServiceAdmin } from '../utils/accessControl';
import {
  buildRepairRequestReadModel,
  normalizeRepairRequestSectionHint,
} from '../utils/repairRequestReadModel';
import { getServiceQuickReplies } from '../utils/chatQuickReplies';
import { isServiceWorkspaceRequestDetail } from '../utils/navigationActiveState';

const REQUEST_DETAIL_SHELL_CLASS =
  'w-full max-w-7xl px-2.5 [padding-left:max(env(safe-area-inset-left),0.625rem)] [padding-right:max(env(safe-area-inset-right),0.625rem)] sm:px-3 lg:px-5';

const normalizeReturnTo = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim();
  if (!normalized.startsWith('/')) {
    return null;
  }

  if (!normalized.startsWith('/app/')) {
    return null;
  }

  return normalized;
};

const buildOverviewSearch = (search: string): string => {
  const nextParams = new URLSearchParams(search);
  nextParams.delete('section');
  nextParams.delete('focus');
  nextParams.delete('review');
  const nextSearch = nextParams.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

const RepairRequestDetailsPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const { currentUser, userProfile, claims } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [repairRequest, setRepairRequest] = useState<RepairRequest | null>(null);
  const [existingReview, setExistingReview] = useState<Review | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSubmittedSuccessfully, setReviewSubmittedSuccessfully] = useState(false);
  const [serviceName, setServiceName] = useState<string | null>(null);
  const [servicePhoneNumber, setServicePhoneNumber] = useState<string | null>(null);
  const [loadingCounterpartLabel, setLoadingCounterpartLabel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const directWorkshopBaselineArmedRef = useRef(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const hasServiceWorkspaceDetailHint = useMemo(
    () =>
      isServiceWorkspaceRequestDetail({
        pathname: location.pathname,
        search: location.search,
      }),
    [location.pathname, location.search]
  );
  const reviewRequested = searchParams.get('review') === '1';
  const sectionHint = normalizeRepairRequestSectionHint(searchParams.get('section'), {
    reviewRequested,
  });
  const focusHint = searchParams.get('focus');
  const requestedReturnTo = normalizeReturnTo(searchParams.get('returnTo'));
  const currentUserId = currentUser?.uid || null;
  const repairRequestClientId = repairRequest?.clientId || null;
  const repairRequestStatus = repairRequest?.status || null;
  const assignedServiceUserIdForProfile = useMemo(() => {
    if (!repairRequest) {
      return null;
    }

    return buildRepairRequestReadModel(repairRequest).assignment.assignedServiceUserId || null;
  }, [repairRequest]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [requestId]);

  useEffect(() => {
    if (!requestId) {
      setError('Nieprawidłowe ID zlecenia w adresie URL.');
      setLoading(false);
      return;
    }

    if (!currentUser) {
      setLoading(false);
      return;
    }

    let isActive = true;
    setLoading(true);
    setError(null);
    setRepairRequest(null);
    setExistingReview(null);
    setReviewLoading(false);
    setReviewSubmittedSuccessfully(false);
    setServiceName(null);
    setServicePhoneNumber(null);
    setLoadingCounterpartLabel(false);

    const unsubscribe = repairRequestService.subscribeToRequestDetails(
      requestId,
      (nextRequest) => {
        if (!isActive) {
          return;
        }

        const readModel = buildRepairRequestReadModel(nextRequest);
        const assignedServiceUserId = readModel.assignment.assignedServiceUserId;
        const isClient = currentUser.uid === nextRequest.clientId;
        const isService =
          currentUser.uid === assignedServiceUserId && isApprovedServiceAdmin(userProfile, claims);

        if (!isClient && !isService) {
          setError('Nie masz uprawnień do wyświetlenia tego zlecenia.');
          setLoading(false);
          return;
        }

        setReviewLoading(isClient && nextRequest.status === RepairStatus.COMPLETED);
        setRepairRequest(nextRequest);
        setError(null);
        setLoading(false);
      },
      (loadError) => {
        if (!isActive) {
          return;
        }

        console.error('Error subscribing to request details:', loadError);
        setError('Wystąpił błąd podczas ładowania szczegółów zlecenia.');
        setLoading(false);
      }
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [claims, currentUser, requestId, userProfile]);

  useEffect(() => {
    if (!repairRequestClientId || !currentUserId) {
      setServiceName(null);
      setServicePhoneNumber(null);
      setLoadingCounterpartLabel(false);
      return;
    }

    const isClient = currentUserId === repairRequestClientId;
    const isService =
      currentUserId === assignedServiceUserIdForProfile &&
      isApprovedServiceAdmin(userProfile, claims);

    if ((!isClient && !isService) || !assignedServiceUserIdForProfile) {
      setServiceName(null);
      setServicePhoneNumber(null);
      setLoadingCounterpartLabel(false);
      return;
    }

    let isActive = true;
    setLoadingCounterpartLabel(isClient);

    const cachedProfile = serviceProviderService.getCachedServiceProviderProfile(
      assignedServiceUserIdForProfile
    );
    const cachedName =
      typeof cachedProfile?.serviceName === 'string' && cachedProfile.serviceName.trim()
        ? cachedProfile.serviceName.trim()
        : null;
    const cachedPhoneNumber =
      typeof cachedProfile?.phoneNumber === 'string' && cachedProfile.phoneNumber.trim()
        ? cachedProfile.phoneNumber.trim()
        : null;
    if (cachedName) {
      setServiceName(cachedName);
    }
    if (cachedPhoneNumber) {
      setServicePhoneNumber(cachedPhoneNumber);
    }

    void serviceProviderService
      .getServiceProviderProfile(assignedServiceUserIdForProfile)
      .then((profile) => {
        if (!isActive) {
          return;
        }
        setServiceName(profile?.serviceName?.trim() || null);
        setServicePhoneNumber(profile?.phoneNumber?.trim() || null);
      })
      .catch((serviceError) => {
        if (!isActive) {
          return;
        }
        console.error('Error fetching service provider label:', serviceError);
        setServiceName(cachedName || null);
        setServicePhoneNumber(cachedPhoneNumber || null);
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setLoadingCounterpartLabel(false);
      });

    return () => {
      isActive = false;
    };
  }, [assignedServiceUserIdForProfile, claims, currentUserId, repairRequestClientId, userProfile]);

  useEffect(() => {
    if (!repairRequestClientId || !currentUserId || !requestId) {
      setExistingReview(null);
      setReviewLoading(false);
      return;
    }

    const isClient = currentUserId === repairRequestClientId;

    if (!isClient || repairRequestStatus !== RepairStatus.COMPLETED) {
      setExistingReview(null);
      setReviewLoading(false);
      return;
    }

    let isActive = true;
    setReviewLoading(true);
    void reviewService
      .getReviewForRequest(requestId)
      .then((review) => {
        if (!isActive) {
          return;
        }
        setExistingReview(review);
      })
      .catch((reviewError) => {
        if (!isActive) {
          return;
        }
        console.error('Error fetching review for request:', reviewError);
        setExistingReview(null);
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setReviewLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [currentUserId, repairRequestClientId, repairRequestStatus, requestId]);

  const detailContext = useMemo(() => {
    if (!repairRequest || !currentUser) {
      return null;
    }

    const readModel = buildRepairRequestReadModel(repairRequest);
    const assignedServiceUserId = readModel.assignment.assignedServiceUserId;
    const isClient = currentUser.uid === repairRequest.clientId;
    const isService =
      currentUser.uid === assignedServiceUserId && isApprovedServiceAdmin(userProfile, claims);

    if (!isClient && !isService) {
      return null;
    }

    const actor: 'client' | 'service' = isClient ? 'client' : 'service';
    const counterpartLabel =
      actor === 'service'
        ? `Właściciel zgłoszenia: ${
            repairRequest.clientDetails?.displayName ||
            repairRequest.clientDetails?.email ||
            'Brak danych'
          }`
        : `Serwis: ${serviceName || 'Brak danych'}`;
    const fallbackReturnTo = actor === 'service' ? '/app/service' : '/app/my-requests';
    const backTarget = requestedReturnTo || fallbackReturnTo;
    const backLabel = actor === 'service' ? '← WYJDŹ' : 'Wróć do moich zgłoszeń';
    const quickReplies =
      actor === 'service'
        ? getServiceQuickReplies({
            serviceName,
            phoneNumber: servicePhoneNumber,
          })
        : [];

    return {
      actor,
      counterpartLabel,
      backTarget,
      backLabel,
      requestIdentity: readModel.requestId || requestId || '',
      quickReplies,
    };
  }, [
    claims,
    currentUser,
    repairRequest,
    requestId,
    requestedReturnTo,
    serviceName,
    servicePhoneNumber,
    userProfile,
  ]);

  useEffect(() => {
    if (!detailContext || detailContext.actor !== 'service' || hasServiceWorkspaceDetailHint) {
      return;
    }

    const nextParams = new URLSearchParams(location.search);
    nextParams.set('workspace', 'service');
    const nextSearch = nextParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      {
        replace: true,
        state: location.state ?? undefined,
      }
    );
  }, [
    detailContext,
    hasServiceWorkspaceDetailHint,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const hasActiveWorkshopIntent = Boolean(sectionHint || focusHint);
    const locationState = location.state as
      | { workshopOverviewBaselineReady?: boolean; workshopResumeSearch?: string }
      | null
      | undefined;

    if (
      !detailContext ||
      detailContext.actor !== 'service' ||
      !hasServiceWorkspaceDetailHint ||
      !hasActiveWorkshopIntent ||
      locationState?.workshopOverviewBaselineReady ||
      directWorkshopBaselineArmedRef.current
    ) {
      return;
    }

    directWorkshopBaselineArmedRef.current = true;
    const activeSearch = location.search;
    const overviewSearch = buildOverviewSearch(activeSearch);

    navigate(
      {
        pathname: location.pathname,
        search: overviewSearch,
      },
      {
        replace: true,
        state: {
          workshopOverviewBaselineReady: true,
          workshopResumeSearch: activeSearch,
        },
      }
    );

    window.setTimeout(() => {
      navigate(
        {
          pathname: location.pathname,
          search: activeSearch,
        },
        {
          replace: false,
          state: {
            workshopOverviewBaselineReady: true,
          },
        }
      );
    }, 0);
  }, [
    detailContext,
    focusHint,
    location.pathname,
    location.search,
    location.state,
    navigate,
    sectionHint,
    hasServiceWorkspaceDetailHint,
  ]);

  const handleSectionChange = useCallback(
    (nextSection: string | null, mode: 'push' | 'replace' = 'replace') => {
      const nextParams = new URLSearchParams(location.search);
      if (nextSection) {
        nextParams.set('section', nextSection);
        nextParams.delete('focus');
      } else {
        nextParams.delete('section');
        nextParams.delete('focus');
      }
      nextParams.delete('review');

      const nextSearch = nextParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        {
          replace: mode !== 'push',
          state: {
            workshopOverviewBaselineReady: true,
          },
        }
      );
    },
    [location.pathname, location.search, navigate]
  );

  if (loading) {
    return (
      <Section className={`${REQUEST_DETAIL_SHELL_CLASS} py-8 md:py-12`}>
        <div className="mx-auto max-w-2xl">
          <PageLoadingState
            title="Ładowanie szczegółów zlecenia"
            message="Pobieramy aktualny stan sprawy."
          />
        </div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section className={`${REQUEST_DETAIL_SHELL_CLASS} py-8 md:py-12`}>
        <div className="mx-auto max-w-xl">
          <PageErrorState
            title="Nie udało się otworzyć zlecenia"
            message={error}
            actionLabel="Wróć"
            onAction={() => navigate(-1)}
          />
        </div>
      </Section>
    );
  }

  if (!repairRequest || !detailContext) {
    return (
      <Section className={`${REQUEST_DETAIL_SHELL_CLASS} py-8 md:py-12`}>
        <div className="mx-auto max-w-xl">
          <PageNotFoundState
            title="Nie znaleziono zlecenia"
            message="Ta sprawa mogła zostać usunięta albo nie masz już do niej dostępu."
            actionLabel="Wróć do listy"
            onAction={() => navigate('/app')}
          />
        </div>
      </Section>
    );
  }

  return (
    <Section className={`${REQUEST_DETAIL_SHELL_CLASS} py-3 md:py-6`}>
      <div className="space-y-4 md:space-y-5">
        <RepairRequestOrderCard
          requestId={detailContext.requestIdentity}
          request={repairRequest}
          actor={detailContext.actor}
          currentUserId={currentUser!.uid}
          counterpartLabel={detailContext.counterpartLabel}
          sectionHint={sectionHint}
          focusHint={focusHint}
          reviewRequested={reviewRequested}
          showReview={detailContext.actor === 'client'}
          existingReview={existingReview}
          reviewLoading={reviewLoading}
          reviewSubmittedSuccessfully={reviewSubmittedSuccessfully}
          onReviewAdded={(review) => {
            setExistingReview(review);
            setReviewLoading(false);
            setReviewSubmittedSuccessfully(true);
          }}
          onSectionChange={handleSectionChange}
          onBack={() => navigate(detailContext.backTarget)}
          backLabel={detailContext.backLabel}
          variant="detail"
          isExpanded
          serviceDetailMode={detailContext.actor === 'service'}
          loadingCounterpartLabel={detailContext.actor === 'client' && loadingCounterpartLabel}
          quickReplies={detailContext.quickReplies}
        />
      </div>
    </Section>
  );
};

export default RepairRequestDetailsPage;
